import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Grid, Typography, AppBar, Toolbar, Paper, CircularProgress, Hidden, Theme, makeStyles } from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';

import { ServerFailed } from '../errors/serverFailed'
import { ShareService } from '../../services/ShareService';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        background: '#efefef',
        paddingBottom: theme.spacing(2),
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        overflowY: 'auto'
    },
    appBar: {
        color: '#6b6b6b',
        background: '#FFFFFF',
        boxShadow: "unset",
        paddingTop: '4px'
    },
    sermonContent: {
        [theme.breakpoints.up("sm")]: {
            margin: theme.spacing(4),
            marginBottom: 8,
            padding: theme.spacing(4),
            width: '90vw',
            minHeight: '85vh'
        },
        [theme.breakpoints.down("xs")]: {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
            marginBottom: 15,
            marginTop: 10,
            minHeight: '75vh',
            width: '100%',
            overflowWrap: 'break-word'
        },
        "& slide": {
            border: "unset",
            lineHeight: "unset"
        }
    },
    grow: {
        flex: 1
    },
    title: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        width: '100%',
        textOverflow: 'ellipsis'
    },
    footerText: {
        fontSize: 11,
    },
    footerLink: {
        color: "#222"
    }
}));

export function ShareHome() {
    const params = useParams<{id: string}>();
    const [loading, setLoading] = useState(true);
    const [serverFailed, setServerFailed] = useState(false);
    const [pageNotFound, setPageNotFound] = useState(false);
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const classes = useStyles();

    useEffect(() => {
        async function getSharedAsset(){
            document.title = "Sermon Maker";
            
            setLoading(true);
            const shareResponse = await ShareService.getSharedAsset(params.id.toLowerCase());
            setLoading(false);

            if(shareResponse.error){
                if(shareResponse.statusCode === 404) return setPageNotFound(true);
                return setServerFailed(true);
            }
            
            const builderAsset = shareResponse.data;
            setBody(builderAsset.text);
            setTitle(builderAsset.name);
            document.title = `${builderAsset.name}`;
            
            let removeHtmlTags = /(<([^>]+)>)/ig;
            let description = builderAsset.text.replace(removeHtmlTags, " ").replace(/\s\s+/g, " ").trim();
            let existingMeta = document.querySelector("meta[name=description]") as HTMLMetaElement;
            if (!existingMeta) {
                let meta = document.createElement('meta');
                meta.name = "description";
                meta.content = description.substring(0, 160);
                document.getElementsByTagName('head')[0].appendChild(meta);
            } else {
                existingMeta.content = description.substring(0, 160);
            }
        };

        getSharedAsset();
    }, [])

    if (pageNotFound) {
        return (
            <Grid container>
                <Grid item xs={12} sm={6} style={{ margin: '5% auto' }}>
                    <Typography variant='h6' color="textSecondary" align='center'>
                        <ErrorIcon style={{ marginTop: "50px", width: "170px", height: "170px", color: "#CCC" }} />
                        <br />
                        Oops, it looks like something went wrong. This resource is no longer being shared by the owner.
                        <br />
                        Learn more about <a href="https://www.sermonmaker.net/">Sermon Maker</a>
                    </Typography>
                </Grid>
            </Grid>
        );
    }
    if (serverFailed) {
        return (
            <ServerFailed errorMessage="We're sorry, but we're having trouble retrieving this resource." />
        );
    }
    return (
        <div className={classes.container}>
            <AppBar position="static" className={classes.appBar}>
                <Toolbar>
                    <Hidden mdUp>
                        <div style={{ flexGrow: 1, width: '50%' }}>
                            <Typography variant='h6' className={classes.title} color="inherit">{title}</Typography>
                        </div>
                    </Hidden>
                    <Hidden mdDown>
                        <div style={{ flexGrow: 1 }}>
                            <Typography variant='h6' className={classes.title} color="inherit">{title}</Typography>
                        </div>
                    </Hidden>
                    <div className={classes.grow} />
                </Toolbar>
            </AppBar>
            <Grid container direction="row" justifyContent="center" alignItems="flex-start">
                <Paper className={classes.sermonContent} id="content" elevation={2}>
                    {loading ?
                        <div style={{ textAlign: 'center' }}>
                            <CircularProgress style={{ marginTop: "50px" }} />
                            <br />
                            <Typography variant="body1" color="textSecondary" style={{ textAlign: 'center' }}>
                                Loading...
                            </Typography>
                        </div>
                        :
                        <>
                            <div dangerouslySetInnerHTML={body.length > 0 ? { __html: body } : undefined} />
                            <div style={{ padding: 20 }} />
                        </>
                    }
                </Paper>
                <div>
                    <Typography align="center" className={classes.footerText} >Copyright © 2003-{new Date().getFullYear()} | Outreach, Inc., All rights reserved</Typography>
                    <Typography align="center" className={classes.footerText}>
                        <a href="https://www.sermonmaker.net/" className={classes.footerLink}>Sermon Maker</a> •
                    <a href="https://www.sermonmaker.net/contact-us/" className={classes.footerLink}> Contact Us</a> •
                    <a href="https://www.sermoncentral.com/Content/privacy-policy" className={classes.footerLink}> Privacy Policy</a> •
                    <a href="https://www.sermoncentral.com/Content/termsandconditions" className={classes.footerLink}> Terms of Use</a>
                    </Typography>
                </div>
            </Grid>
        </div>
    );
}